import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { Cell, Grid } from 'formlabs-web-components/Grid';
import DisplayComponent from '../../BaseComponents/DisplayComponent';
import GenericCarousel from './GenericCarousel';
import Picture from '../Picture';

import useIsMobile from '../../../../hooks/useIsMobile';

import playIcon from '../../../../../../public/ic-play.svg';
import pauseIcon from '../../../../../../public/ic-pause.svg';
import './Carousel.module.css';

const StoreCarousel = ({
  children,
  controlsPosition,
  autoplay,
  stretchChildren,
  fullHeight,
  draggable,
  customClasses
}) => {
  const { t } = useTranslation();
  const mobile = useIsMobile(640);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentDirection, setCurrentDirection] = useState(1);
  const [isSliding, setIsSliding] = useState(false);
  const [isPlaying, setIsPlaying] = useState(autoplay);

  const Arrow = ({ direction = 'left' }) => (
    <span styleName={`arrow-${direction}`} />
  );

  return (
    <>
      <div
        styleName={classNames('carousel-container', {
          'full-height': fullHeight
        })}
        className={customClasses}
      >
        <GenericCarousel
          stretchChildren={stretchChildren}
          fullHeight={fullHeight}
          draggable={draggable}
          initialSlide={0}
          nextSlide={currentSlide}
          nextDirection={currentDirection}
          onSlideBegin={nextSlide => {
            setCurrentSlide(nextSlide);
            setIsSliding(true);
          }}
          onSlideEnd={() => {
            setIsSliding(false);
          }}
          autoplaying={isPlaying}
          autoplayInterval={5000}
        >
          {children}
        </GenericCarousel>
        <div styleName={classNames('controls', controlsPosition)}>
          {autoplay && (
            <button
              onClick={() => setIsPlaying(!isPlaying)}
              aria-label={
                isPlaying ? t('Pause slideshow') : t('Play slideshow')
              }
              styleName={classNames({ play: !isPlaying, pause: isPlaying })}
            >
              <img
                src={isPlaying ? pauseIcon : playIcon}
                styleName="icon"
                alt=""
                role="presentation"
              />
            </button>
          )}
          <button
            styleName="prev"
            aria-label={t('Show previous slide')}
            onClick={e => {
              e.stopPropagation();
              if (!isSliding) {
                setCurrentSlide(currentSlide - 1);
                setCurrentDirection(-1);
              }
            }}
          >
            {controlsPosition === 'internal' && <Arrow direction="left" />}
          </button>
          <button
            styleName="next"
            aria-label={t('Show next slide')}
            onClick={e => {
              e.stopPropagation();
              if (!isSliding) {
                setCurrentSlide(currentSlide + 1);
                setCurrentDirection(1);
              }
            }}
          >
            {controlsPosition === 'internal' && <Arrow direction="right" />}
          </button>
        </div>
      </div>
      {!mobile && (
        <div styleName="thumbnails">
          <Grid direction="x">
            {children.map((child, index) => (
              <Cell mediumSize={4} key={`store image ${index}`}>
                <button
                  styleName={classNames({
                    active: index === currentSlide
                  })}
                  aria-label={t('Show slide {{index}}', { index: index + 1 })}
                  onClick={e => {
                    e.stopPropagation();
                    if (!isSliding) {
                      setCurrentSlide(index);
                    }
                  }}
                >
                  {child}
                </button>
              </Cell>
            ))}
          </Grid>
        </div>
      )}
      {mobile && (
        <div styleName="controls bottom">
          <div styleName="dots center">
            {children.map((child, index) => (
              <button
                key={index}
                styleName={classNames({
                  dot: true,
                  active: index === currentSlide
                })}
                aria-label={t('Show slide {{index}}', { index: index + 1 })}
                onClick={e => {
                  e.stopPropagation();
                  if (!isSliding) {
                    setCurrentSlide(index);
                  }
                }}
              />
            ))}
          </div>
        </div>
      )}
    </>
  );
};

StoreCarousel.propTypes = {
  controlsPosition: PropTypes.string,
  customClasses: PropTypes.node,
  children: PropTypes.node
};

export default DisplayComponent(StoreCarousel);
